import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { token, tenantSlug } = useAuthStore.getState();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  if (tenantSlug) {
    config.headers.set('X-Tenant-Slug', tenantSlug);
  }
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug && !tenantSlug) {
    config.headers.set('X-Tenant-Slug', envSlug);
  }
  return config;
});

// Shape que devuelve el backend en /auth/login y /auth/refresh.
interface BackendAuthSession {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    role: 'master' | 'admin' | 'operator' | 'viewer';
    tenantId: string;
    tenantSlug: string;
  };
  tokens: { accessToken: string; refreshToken: string };
}

// Single-flight: varios 401 simultáneos (el panel dispara GETs en paralelo) comparten UN
// solo refresh y esperan el mismo resultado. Evita el "token-loop" y no pisa la rotación
// del refreshToken (el backend rota el refresh en cada uso).
let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const state = useAuthStore.getState();
  const rt = state.refreshToken;
  if (!rt) return null;
  try {
    // apiClient, pero el interceptor de abajo excluye /auth/refresh del retry → no hay loop.
    const { data } = await apiClient.post<BackendAuthSession>('/auth/refresh', { refreshToken: rt });
    state.setSession({
      user: {
        id: data.user.id,
        username: data.user.username,
        display_name: data.user.displayName ?? undefined,
        role: data.user.role,
        permissions: [],
      },
      token: data.tokens.accessToken,
      refreshToken: data.tokens.refreshToken,
      tenantId: data.user.tenantId,
      tenantSlug: data.user.tenantSlug,
    });
    return data.tokens.accessToken;
  } catch {
    state.logout();
    return null;
  }
}

// Ante 401: renovar el accessToken con el refresh (7 días) y reintentar el request original.
// Sólo si el refresh TAMBIÉN falla (vencido/revocado) se desloguea y va a /login.
apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retry && !isRefreshCall) {
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        original._retry = true;
        if (!refreshing) {
          refreshing = doRefresh().finally(() => {
            refreshing = null;
          });
        }
        const newToken = await refreshing;
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return apiClient(original);
        }
        // Refresh falló → sesión realmente vencida.
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(error);
  },
);
